import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { METHOD_LABELS } from '../lib/format';
import type { NavCounts } from '../lib/types';
import { GlobalSearch } from './GlobalSearch';
import { Icon } from './icons';
import { IconButton } from './IconButton';
import { allows, NAV, NO_PROPERTY_PAGES, PURCHASING_SEGMENTS, TABS, placeOf, tabOf } from './nav';
import { SegmentedNav } from './Segmented';
import { ShellContext, useTheme } from './shellContext';
import { SplitButton } from './SplitButton';
import { Modal } from './ui';

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
}

export function roleName(user: { role: string; roleName?: string }) {
  return user.roleName ?? user.role.charAt(0).toUpperCase() + user.role.slice(1);
}

// From PO asks for the order first (and is the main Stock in action); Without PO goes straight to free lines.
const STOCK_IN = '/stock/move?mode=in&po=choose';
const STOCK_IN_OPTIONS = [{ label: 'From PO', to: STOCK_IN }, { label: 'Without PO', to: '/stock/move?mode=in&po=none' }];

/**
 * App frame. Desktop (768px and up): charcoal sidebar (property, menu, user and Settings gear) and a 64px top bar
 * (breadcrumb, search, Stock out, Stock in split). Phone: a header (title, back on detail pages, search) and five
 * bottom tabs. Both are in the page; CSS shows one or the other.
 */
export function Shell() {
  const { me, property, setPropertyId, can, signOut } = useSession();
  const { theme, next: nextTheme } = useTheme();
  const { pathname } = useLocation();
  const [title, setTitle] = useState<string | null>(null);
  const [counts, setCounts] = useState<NavCounts | null>(null);
  const [userMenu, setUserMenu] = useState(false);
  const [searching, setSearching] = useState(false);
  // nextTheme only calls setTheme with an updater, so the value changes with the theme alone.
  const shell = useMemo(() => ({ setTitle, theme, nextTheme }), [theme]);
  const propertyId = property?.id;

  useEffect(() => setUserMenu(false), [pathname]);
  // Badges follow what people do, so they refresh on every page change.
  useEffect(() => {
    if (!propertyId) return;
    let live = true;
    api.get<NavCounts>('/nav/counts', { propertyId }).then(c => live && setCounts(c), () => live && setCounts(null));
    return () => { live = false; };
  }, [propertyId, pathname]);

  if (!me) return null;
  const place = placeOf(pathname);
  const tab = tabOf(pathname);
  const home = place.item?.to === '/';
  const pageName = home ? property?.name : place.item?.label ?? (pathname === '/more' ? 'More' : null);
  const crumbs = [
    home ? property?.name : place.group,
    place.item && (place.detail ? <Link to={place.item.to}>{place.item.label}</Link> : place.item.label),
    (place.detail || !place.item) && title,
  ].filter(Boolean).filter((c, i, all) => c !== all[i - 1]);
  const heading = (place.detail ? title : null) ?? pageName ?? title ?? 'Inventory';
  const back = place.detail && place.item ? place.item.to : place.group === 'Settings' ? '/more' : null;
  const settingsActive = place.group === 'Settings';
  const canMove = can('stock_move') && !!property;

  return (
    <ShellContext.Provider value={shell}>
      <div className="app">
        <nav className="sidebar" aria-label="Main">
          <div className="brand">
            <img src="/hotel101-white.png" alt="Hotel101" />
            <span>Inventory</span>
          </div>
          {property && (
            <div className="sideprop">
              <div className="navgroup" aria-hidden="true">Property</div>
              {me.properties.length > 1 ? (
                <select className="propselect" aria-label="Property" value={property.id} onChange={e => setPropertyId(e.target.value)}>
                  {me.properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              ) : (
                <div className="propname">{property.name}</div>
              )}
              <span className="propmeta">{METHOD_LABELS[property.valuation_method]} · {property.currency}</span>
            </div>
          )}
          {NAV.map(group => {
            const items = group.items.filter(i => allows(can, i.can));
            if (!items.length) return null;
            return (
              <div key={group.group || 'top'} data-group={group.group || 'Home'}>
                {group.group && <div className="navgroup">{group.group}</div>}
                {items.map(i => {
                  const badge = i.badge && counts ? counts[i.badge.key] : 0;
                  const active = place.item === i;
                  return (
                    <Link key={i.to} to={i.to} className={`navlink${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}
                      aria-label={badge > 0 ? `${i.label}, ${badge} ${i.badge!.meaning}` : undefined}>
                      <Icon name={i.icon} />{i.label}
                      {badge > 0 && <span className="navbadge" aria-hidden="true">{badge}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
          <div className="spacer" />
          <div className="navfoot">
            <button type="button" className="userbutton" aria-expanded={userMenu} aria-controls="user-menu" onClick={() => setUserMenu(o => !o)}>
              <span className="avatar" aria-hidden="true">{initials(me.user.name)}</span>
              <span className="who"><span className="navuser">{me.user.name}</span><span className="small">{roleName(me.user)}</span></span>
            </button>
            <Link to="/settings" aria-label="Settings" title="Settings" className={`gear${settingsActive ? ' active' : ''}`} aria-current={settingsActive ? 'page' : undefined}>
              <Icon name="gear" size={18} />
            </Link>
            {userMenu && (
              <div id="user-menu" className="usermenu">
                <Link to="/account">My account</Link>
                <button type="button" onClick={nextTheme}>Theme: {theme}</button>
                <button type="button" onClick={() => void signOut()}>Sign out</button>
              </div>
            )}
          </div>
        </nav>

        <div className="main">
          <header className="topbar">
            <nav aria-label="Breadcrumb" className="crumbs">
              <ol>{crumbs.map((c, i) => <li key={i} aria-current={i === crumbs.length - 1 ? 'page' : undefined}>{c}</li>)}</ol>
            </nav>
            {property && <GlobalSearch />}
            <div className="grow" />
            {canMove && (
              <div className="actions topactions">
                <Link to="/stock/move?mode=out&type=issue" className="button secondary">Stock out</Link>
                <SplitButton label="Stock in" to={STOCK_IN} options={STOCK_IN_OPTIONS} menuLabel="Stock in options" />
              </div>
            )}
          </header>

          <header className="phonehead">
            {back ? <Link to={back} className="iconbutton" aria-label="Back"><Icon name="back" size={20} /></Link> : <span className="slot" />}
            <span className="title" aria-hidden="true">{heading}</span>
            {property ? <IconButton icon="search" label="Search" onClick={() => setSearching(true)} /> : <span className="slot" />}
          </header>

          <main className="content">
            {place.group === 'Purchasing' && <div className="phoneonly subnav"><SegmentedNav label="Purchasing" links={PURCHASING_SEGMENTS} /></div>}
            {property ? <Outlet key={property.id} /> : NO_PROPERTY_PAGES.includes(pathname) ? <Outlet /> : can('admin') ? (
              <div className="notice warn">No properties yet. <Link to="/properties">Create the first property</Link> to start.</div>
            ) : (
              <div className="notice warn">You are not assigned to any property yet. Ask an administrator to give you access.</div>
            )}
          </main>
        </div>

        <nav className="tabbar" aria-label="Tabs">
          {TABS.filter(t => !t.can || can(t.can)).map(t => (
            <Link key={t.key} to={t.to} className={t.key === tab ? 'on' : undefined} aria-current={t.key === tab ? 'page' : undefined}>
              <Icon name={t.icon} size={20} /><span>{t.label}</span>
            </Link>
          ))}
        </nav>

        {searching && (
          <Modal title="Search" onClose={() => setSearching(false)}>
            <GlobalSearch autoFocus onDone={() => setSearching(false)} />
          </Modal>
        )}
      </div>
    </ShellContext.Provider>
  );
}
